import React from "react";
import { Card, Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileInvoice } from "@fortawesome/free-solid-svg-icons";
import { ikonIndikator } from "../../utils/indicatorIcon";
import { formatAngka } from "../../utils";

/**
 * Blok headline sesuai bagian 13 dokumen alur: jumlah order, lalu indikator
 * lingkungan utama, lalu blok social impact.
 *
 * Urutan kartunya datang dari `sort_order` di registry indikator, bukan dari
 * berkas ini — sama seperti urutan pada struk pelanggan. Jadi menyusun ulang
 * dashboard dilakukan dari menu Indikator, bukan dengan mengubah kode.
 *
 * Kartu penghematan menampilkan `total_saved`, kartu konsumsi menampilkan
 * `total`. Keduanya tetap disertai intensitas per kg, karena angka absolut
 * naik seiring pertumbuhan bisnis dan tidak menjawab apakah operasinya membaik.
 */
/**
 * Cadangan bila server belum mengirim `panel_social_items` (mis. admin ini
 * dipakai terhadap API versi lama). Label di sini sengaja tidak dipakai bila
 * server sudah mengirim daftarnya, karena nama indikator adalah milik registry
 * — menuliskannya mati di halaman membuat penggantian nama dari menu Indikator
 * tidak berpengaruh apa pun.
 */
const SOCIAL_CADANGAN = [
  ["EMPLOYMENT", "Total People Employed", "total_employed", "orang", "users"],
  ["EMPLOYMENT", "New Jobs Created", "new_jobs_created", "posisi", "new"],
  ["EMPLOYMENT", "Employment Hours", "employment_hours", "jam", "clock"],
  ["INCLUSION", "People with Disabilities Employed", "disability_workers", "orang", "accessible"],
  ["COMMUNITY", "Local Workers", "local_workers", "orang", "home"],
];


const warnaTier = (tier) =>
  ({ T1: "success", T2: "primary", T3: "warning", T4: "danger" }[tier] ||
  "secondary");

function KartuAngka({ ikon, judul, nilai, unit, sub, tier }) {
  return (
    <Col xs={6} md={4} xl={3}>
      <Card className="h-100 shadow-sm border-0">
        <Card.Body className="p-3">
          <div className="d-flex justify-content-between align-items-start">
            <div
              className="text-muted text-uppercase fw-bold"
              style={{ fontSize: "10px", letterSpacing: "0.5px" }}
            >
              <FontAwesomeIcon icon={ikon} className="me-2" />
              {judul}
            </div>
            {tier ? (
              <span className={`badge bg-${warnaTier(tier)}`}>{tier}</span>
            ) : null}
          </div>

          <div className="mt-2">
            <span className="fw-bold" style={{ fontSize: "22px" }}>
              {formatAngka(nilai)}
            </span>
            {unit ? (
              <span className="ms-1 text-muted" style={{ fontSize: "12px" }}>
                {unit}
              </span>
            ) : null}
          </div>

          {sub ? (
            <div className="text-muted" style={{ fontSize: "11px" }}>
              {sub}
            </div>
          ) : null}
        </Card.Body>
      </Card>
    </Col>
  );
}

function PanelHeadline({ totalOrder, items = [], social, socialItems = [] }) {
  // Bagan social impact bercabang tiga; kartunya dikelompokkan mengikuti itu
  // supaya terbaca sebagai tiga hal berbeda, bukan lima angka sederet.
  const daftarSocial = socialItems.length
    ? socialItems
    : SOCIAL_CADANGAN.map(([grup, nama_en, key, unit, icon]) => ({
        code: key,
        nama_en,
        unit,
        icon,
        grup,
        nilai: social ? social[key] : null,
      }));


  return (
    <>
      <Row className="g-2">
        <KartuAngka
          ikon={faFileInvoice}
          judul="Orders"
          nilai={totalOrder}
          unit="order"
          sub="pada periode ini"
        />

        {items.map((val) => {
          // Indikator penghematan diukur dari total_saved; sisanya dari total.
          const hemat = val.code.includes("SAVED") || val.code.includes("AVOIDED");
          // Server sudah menskalakan satuan untuk level periode (ml → L,
          // g → kg); `total` mentah tetap tersedia untuk ekspor dan grafik.
          const nilai =
            val.nilai_tampil !== undefined
              ? val.nilai_tampil
              : hemat
              ? val.total_saved
              : val.total;
          const satuan = val.unit_tampil || val.unit;
          // Kartu penghematan memakai intensitas penghematan; memakai
          // `intensity` akan menampilkan konsumsi per kg di bawah angka hemat.
          const perKg = hemat ? val.intensity_saved : val.intensity;

          return (
            <KartuAngka
              key={val.code}
              ikon={ikonIndikator(val.icon)}
              judul={val.nama_pendek || val.nama_en || val.nama}
              nilai={nilai}
              unit={satuan}
              tier={val.quality_tier}
              sub={
                perKg !== null && perKg !== undefined
                  ? `${formatAngka(perKg)} ${val.unit}/kg`
                  : null
              }
            />
          );
        })}
      </Row>

      <div className="d-flex align-items-center my-3">
        <div className="flex-grow-1 border-top" />
        <div
          className="px-3 text-muted fw-bold"
          style={{ fontSize: "11px", letterSpacing: "1px" }}
        >
          SOCIAL IMPACT
        </div>
        <div className="flex-grow-1 border-top" />
      </div>

      {social ? (
        <Row className="g-2">
          {daftarSocial.map((val) => (
            <KartuAngka
              key={val.code}
              ikon={ikonIndikator(val.icon || "users")}
              judul={val.nama_pendek || val.nama_en}
              nilai={val.nilai}
              unit={val.unit}
              // Cabang bagan social impact ditempelkan sebagai keterangan
              // kartu. Dijadikan judul kelompok, EMPLOYMENT akan muncul dua
              // kali karena urutan kartunya mengikuti rancangan dashboard.
              sub={val.grup}
            />
          ))}
        </Row>
      ) : (
        <div className="text-muted" style={{ fontSize: "12px" }}>
          Data social impact periode ini belum diisi. Isi lewat panel Social
          Impact di bawah — angkanya kondisi perusahaan pada periode berjalan,
          bukan turunan dari order.
        </div>
      )}
    </>
  );
}

export default PanelHeadline;
